// AUTOGENERIERT durch scripts/copy-wire.mjs — NICHT von Hand editieren.
// Quelle: functions/src/wire/support-wire.ts

// Wire-Contract des Bug-/Vorschlags-Melders — Single Source of Truth für Backend UND Frontend.
//
// Diese Datei ist bewusst **importfrei**: sie wird per prebuild-Hook nach src/generated/
// kopiert (scripts/copy-wire.mjs). Jeder Import hier würde die Kopie im Angular-Projekt brechen.
//
// Warum geteilt: Die Längen- und Größengrenzen setzt der Server durch, aber der Client muss
// dieselben Werte kennen — sonst zeigt er einen Zähler, der bei 4000 Zeichen noch grün ist,
// während der Callable schon ablehnt. Zwei handgeschriebene Fassungen driften.

/** Art einer Meldung. Bestimmt Label und Titel-Präfix des GitHub-Issues. */
export const ISSUE_REPORT_KINDS = ['bug', 'suggestion'] as const;
export type IssueReportKind = (typeof ISSUE_REPORT_KINDS)[number];

export function isIssueReportKind(value: unknown): value is IssueReportKind {
  return typeof value === 'string' && (ISSUE_REPORT_KINDS as readonly string[]).includes(value);
}

/**
 * Zustand der GitHub-Projektion. Bewusst **getrennt** von der Erledigung: ob ein Issue
 * angelegt wurde, hat nichts damit zu tun, ob die Meldung bearbeitet ist. Ein linearer
 * Status würde beides vermischen und wäre bei jedem Retry mehrdeutig.
 */
export const ISSUE_REPORT_GITHUB_STATES = ['pending', 'published', 'failed'] as const;
export type IssueReportGithubState = (typeof ISSUE_REPORT_GITHUB_STATES)[number];

export function isIssueReportGithubState(value: unknown): value is IssueReportGithubState {
  return (
    typeof value === 'string' && (ISSUE_REPORT_GITHUB_STATES as readonly string[]).includes(value)
  );
}

/** Titel-Obergrenze. Landet 1:1 im Issue-Titel hinter dem Präfix. */
export const MAX_REPORT_TITLE_LENGTH = 120;

/** Beschreibungs-Obergrenze. */
export const MAX_REPORT_DESCRIPTION_LENGTH = 4000;

/** Mindestlänge der Beschreibung — „geht nicht" ist keine Meldung, die jemandem hilft. */
export const MIN_REPORT_DESCRIPTION_LENGTH = 10;

/**
 * Harte Obergrenze des dekodierten Screenshots. Der Client rechnet vorher herunter
 * ({@link REPORT_SCREENSHOT_MAX_EDGE_PX}); diese Grenze fängt ab, was trotzdem zu groß ankommt.
 */
export const MAX_REPORT_SCREENSHOT_BYTES = 2 * 1024 * 1024;

/** Kantenlänge, auf die der Client den Screenshot vor dem Senden herunterrechnet. */
export const REPORT_SCREENSHOT_MAX_EDGE_PX = 1600;

/**
 * Bevorzugtes Format: PNG. Ein Screenshot besteht überwiegend aus Text und Flächen — JPEG
 * erzeugt dort sichtbare Ringe genau an den Kanten, die man lesen können muss. Sprengt das
 * PNG die Grenze, weicht der Client auf {@link REPORT_SCREENSHOT_FALLBACK_CONTENT_TYPE} aus.
 */
export const REPORT_SCREENSHOT_CONTENT_TYPE = 'image/png';
export const REPORT_SCREENSHOT_FALLBACK_CONTENT_TYPE = 'image/jpeg';
export const REPORT_SCREENSHOT_FALLBACK_QUALITY = 0.82;

/** Erlaubte Content-Types eines Screenshots — der Server prüft dagegen. */
export const ALLOWED_REPORT_SCREENSHOT_TYPES = [
  REPORT_SCREENSHOT_CONTENT_TYPE,
  REPORT_SCREENSHOT_FALLBACK_CONTENT_TYPE,
] as const;

export function isAllowedScreenshotType(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    (ALLOWED_REPORT_SCREENSHOT_TYPES as readonly string[]).includes(value)
  );
}

/** Kontingent: Meldungen pro Stunde und Konto. */
export const ISSUE_REPORT_LIMIT_PER_HOUR = 5;

/** Seitengröße der Admin-Übersicht. */
export const ISSUE_REPORT_PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// submitIssueReport
// ---------------------------------------------------------------------------

export interface WireSubmitIssueReportRequest {
  kind: IssueReportKind;
  title: string;
  description: string;
  /** Base64 **ohne** `data:`-Präfix. Fehlt, wenn ohne Screenshot gemeldet wird. */
  screenshotBase64?: string;
  screenshotContentType?: string;
  /** Route, auf der gemeldet wurde — reine Pfadangabe, ohne Query. */
  route?: string;
  userAgent?: string;
}

export interface WireSubmitIssueReportResponse {
  reportId: string;
  githubState: IssueReportGithubState;
  /** Fehlt, solange das Issue noch nicht angelegt werden konnte. */
  issueNumber?: number;
}

// ---------------------------------------------------------------------------
// Admin-Ansicht
// ---------------------------------------------------------------------------

/**
 * Listeneintrag. Trägt bewusst **keinen** Screenshot und keine Kontaktdaten — die Übersicht
 * soll überflogen werden können, ohne dass für jede Zeile eine signierte URL entsteht.
 */
export interface WireIssueReportSummary {
  id: string;
  kind: IssueReportKind;
  title: string;
  createdAtMs: number;
  githubState: IssueReportGithubState;
  issueNumber?: number;
  issueUrl?: string;
  resolved: boolean;
  resolvedAtMs?: number;
  reporterName: string;
  hasScreenshot: boolean;
}

export interface WireIssueReportDetail extends WireIssueReportSummary {
  description: string;
  /**
   * Die personenfreie Fassung, die im Issue steht. Fehlt, solange die Zusammenfassung noch
   * nicht gelungen ist — dann existiert auch kein Issue. Steht in der Detailansicht neben
   * dem Wortlaut, damit nachvollziehbar bleibt, was das Haus verlassen hat.
   */
  publishedTitle?: string;
  publishedDescription?: string;
  route: string;
  userAgent: string;
  environment: string;
  reporterUid: string;
  reporterEmail: string;
  /** Kurzlebige Lese-URL (15 Min), nur für diese eine Detailansicht erzeugt. */
  screenshotUrl?: string;
  /** Letzter GitHub-Fehler, falls `githubState === 'failed'`. */
  githubError?: string;
}

/**
 * Filter der Admin-Übersicht — bewusst **eine** Dimension statt frei kombinierbarer Felder.
 *
 * Jede Kombination aus Filter und `orderBy createdAt` braucht in Firestore einen eigenen
 * zusammengesetzten Index. Drei unabhängige Filter wären acht Indizes für eine Liste, die ein
 * einzelner Admin benutzt. Eine Dimension beantwortet jede reale Frage („was ist offen?",
 * „welche Fehler gibt es?", „wo hakt die GitHub-Anbindung?") und kostet fünf Indizes.
 */
export const ISSUE_REPORT_FILTERS = [
  'all',
  'open',
  'resolved',
  'bug',
  'suggestion',
  'githubFailed',
] as const;
export type IssueReportFilter = (typeof ISSUE_REPORT_FILTERS)[number];

export function isIssueReportFilter(value: unknown): value is IssueReportFilter {
  return typeof value === 'string' && (ISSUE_REPORT_FILTERS as readonly string[]).includes(value);
}

export interface WireListIssueReportsRequest {
  filter?: IssueReportFilter;
  pageSize?: number;
  /** Cursor der Vorseite (`nextCursor`). */
  cursor?: string;
}

export interface WireListIssueReportsResponse {
  items: WireIssueReportSummary[];
  nextCursor: string | null;
}

export interface WireIssueReportIdRequest {
  reportId: string;
}

export interface WireResolveIssueReportResponse {
  reportId: string;
  resolvedAtMs: number;
}

export interface WireDeleteIssueReportResponse {
  reportId: string;
  screenshotDeleted: boolean;
}
